import {
  createContext,
  lazy,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { Download, ExternalLink, X, ZoomIn, ZoomOut, Maximize2 } from "@/shared/icons";
import {
  attachmentDownloadUrl,
  attachmentPreviewUrl,
} from "@/api/hooks/attachments";
import { api } from "@/api/client";
import { cn } from "@/shared/utils/cn";

// Lazy markdown renderer — bundle cost only paid when the first .md opens.
const MarkdownView = lazy(() => import("./MarkdownView"));

type ViewerKind = "image" | "text" | "md";

interface ViewerState {
  id: string;
  filename: string;
  kind: ViewerKind;
}

interface AttachmentViewerContextValue {
  /** Route an attachment to the best surface: image/text/md inline lightbox,
   *  PDF in a new tab, everything else downloads. Mirrors openAttachment(). */
  open: (
    id: string,
    contentType: string | null | undefined,
    filename?: string | null,
  ) => void;
}

const Ctx = createContext<AttachmentViewerContextValue | null>(null);

const TEXT_EXTS = [".txt"];
const MD_EXTS = [".md", ".markdown"];

function endsWithAny(name: string, exts: string[]): boolean {
  const lower = name.toLowerCase();
  return exts.some((e) => lower.endsWith(e));
}

/** Decide the inline kind, or null when the file is not inline-previewable. */
function resolveKind(
  contentType: string | null | undefined,
  filename: string,
): ViewerKind | "pdf" | null {
  const ct = (contentType || "").toLowerCase();
  if (ct.startsWith("image/")) return "image";
  if (ct === "application/pdf" || endsWithAny(filename, [".pdf"])) return "pdf";
  // Markdown wins over plain text — server may tag .md as text/plain or octet.
  if (ct.includes("markdown") || endsWithAny(filename, MD_EXTS)) return "md";
  if (ct === "text/plain" || ct === "text/markdown" || endsWithAny(filename, TEXT_EXTS))
    return "text";
  return null;
}

export function AttachmentViewerProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ViewerState | null>(null);

  const open = useCallback(
    (
      id: string,
      contentType: string | null | undefined,
      filename?: string | null,
    ) => {
      const name = filename || "attachment";
      const kind = resolveKind(contentType, name);
      if (kind === "image" || kind === "text" || kind === "md") {
        setState({ id, filename: name, kind });
      } else if (kind === "pdf") {
        window.open(attachmentPreviewUrl(id), "_blank", "noopener,noreferrer");
      } else {
        // Unknown / non-previewable — force download.
        window.location.href = attachmentDownloadUrl(id);
      }
    },
    [],
  );

  return (
    <Ctx.Provider value={{ open }}>
      {children}
      {state && (
        <ViewerModal
          key={state.id}
          state={state}
          onClose={() => setState(null)}
        />
      )}
    </Ctx.Provider>
  );
}

export function useAttachmentViewer(): AttachmentViewerContextValue {
  const ctx = useContext(Ctx);
  if (!ctx)
    throw new Error("useAttachmentViewer must be used within AttachmentViewerProvider");
  return ctx;
}

const ZOOM_MIN = 1;
const ZOOM_MAX = 5;
const ZOOM_STEP = 0.5;

function ViewerModal({ state, onClose }: { state: ViewerState; onClose: () => void }) {
  const { id, filename, kind } = state;
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const dragging = useRef<{ x: number; y: number } | null>(null);

  // Content fetched via authenticated axios — not a token-in-URL <img src>.
  // The preview response carries `Cross-Origin-Resource-Policy: same-origin`,
  // which blocks a cross-origin <img>/<iframe> subresource (dev: client :5173
  // vs api :8000). A CORS XHR is exempt, so we blob-fetch images too and render
  // from a same-origin object URL.
  const [text, setText] = useState<string | null>(null);
  const [imgUrl, setImgUrl] = useState<string | null>(null);
  const [loadErr, setLoadErr] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;
    if (kind === "image") {
      api
        .get(`/attachments/${id}/preview`, { responseType: "blob" })
        .then((r) => {
          if (cancelled) return;
          objectUrl = URL.createObjectURL(r.data as Blob);
          setImgUrl(objectUrl);
        })
        .catch(() => {
          if (!cancelled) setLoadErr(true);
        });
    } else {
      api
        .get<string>(`/attachments/${id}/preview`, { responseType: "text" })
        .then((r) => {
          if (!cancelled) setText(typeof r.data === "string" ? r.data : String(r.data));
        })
        .catch(() => {
          if (!cancelled) setLoadErr(true);
        });
    }
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [id, kind]);

  const resetZoom = useCallback(() => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }, []);

  const zoomBy = useCallback((delta: number) => {
    setZoom((z) => {
      const next = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, +(z + delta).toFixed(2)));
      if (next === 1) setPan({ x: 0, y: 0 });
      return next;
    });
  }, []);

  const onWheel = (e: React.WheelEvent) => {
    if (kind !== "image") return;
    e.preventDefault();
    zoomBy(e.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP);
  };

  const onPointerDown = (e: React.PointerEvent) => {
    if (kind !== "image" || zoom <= 1) return;
    dragging.current = { x: e.clientX - pan.x, y: e.clientY - pan.y };
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!dragging.current) return;
    setPan({ x: e.clientX - dragging.current.x, y: e.clientY - dragging.current.y });
  };
  const onPointerUp = () => {
    dragging.current = null;
  };

  if (typeof document === "undefined") return null;

  return createPortal(
    <>
      <div className="av-backdrop" onClick={onClose} aria-hidden />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={filename}
        className="av-shell"
        data-testid="attachment-viewer"
      >
        <div className="av-card">
          <div className="av-hd">
            <span className="av-name" title={filename}>
              {filename}
            </span>
            <div className="av-tools">
              {kind === "image" && (
                <>
                  <button
                    type="button"
                    className="av-tool"
                    onClick={() => zoomBy(-ZOOM_STEP)}
                    disabled={zoom <= ZOOM_MIN}
                    aria-label="Zoom out"
                  >
                    <ZoomOut size={16} />
                  </button>
                  <span className="av-zoom">{Math.round(zoom * 100)}%</span>
                  <button
                    type="button"
                    className="av-tool"
                    onClick={() => zoomBy(ZOOM_STEP)}
                    disabled={zoom >= ZOOM_MAX}
                    aria-label="Zoom in"
                  >
                    <ZoomIn size={16} />
                  </button>
                  <button
                    type="button"
                    className="av-tool"
                    onClick={resetZoom}
                    disabled={zoom === 1 && pan.x === 0 && pan.y === 0}
                    aria-label="Reset zoom"
                  >
                    <Maximize2 size={15} />
                  </button>
                  <span className="av-sep" />
                </>
              )}
              <a
                className="av-tool"
                href={attachmentPreviewUrl(id)}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Open in new tab"
              >
                <ExternalLink size={15} />
              </a>
              <a className="av-tool" href={attachmentDownloadUrl(id)} aria-label="Download">
                <Download size={15} />
              </a>
              <button
                type="button"
                className="av-tool av-close"
                onClick={onClose}
                aria-label="Close"
              >
                <X size={16} />
              </button>
            </div>
          </div>

          <div
            className={cn("av-body", kind === "image" && "is-image")}
            onWheel={onWheel}
          >
            {kind === "image" && imgUrl && (
              <img
                src={imgUrl}
                alt={filename}
                className="av-img"
                draggable={false}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerUp}
                style={{
                  transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                  cursor: zoom > 1 ? (dragging.current ? "grabbing" : "grab") : "default",
                }}
              />
            )}
            {loadErr && (
              <div className="av-msg">Could not load preview. Try downloading instead.</div>
            )}
            {!loadErr &&
              ((kind === "image" && imgUrl === null) ||
                (kind !== "image" && text === null)) && (
                <div className="av-msg">Loading…</div>
              )}
            {kind === "text" && text !== null && <pre className="av-pre">{text}</pre>}
            {kind === "md" && text !== null && (
              <Suspense fallback={<div className="av-msg">Loading…</div>}>
                <div className="av-md">
                  <MarkdownView source={text} />
                </div>
              </Suspense>
            )}
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}
